import { useCallback, useEffect, useMemo, useState } from 'react';
import { CatalogueCard } from '../components/catalogue/CatalogueCard';
import { useDemMasterDataRealtime } from '../hooks/useDemMasterDataRealtime';
import {
  CATALOGUE_PAGE_SIZE,
  loadCatalogueItems,
  matchesCatalogueSearch,
  shouldShowCatalogueItem,
  type CatalogueItem,
  type CataloguePriceTier,
} from '../lib/catalogueItems';

function scrollCatalogueToTop() {
  const body = document.querySelector('.catalogue-page__body');
  if (body instanceof HTMLElement) {
    body.scrollTo({ top: 0, behavior: 'smooth' });
    return;
  }
  const page = document.querySelector('.page');
  if (page instanceof HTMLElement) {
    page.scrollTo({ top: 0, behavior: 'smooth' });
    return;
  }
  window.scrollTo({ top: 0, behavior: 'smooth' });
}

export function CataloguePage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [enrichError, setEnrichError] = useState<string | null>(null);
  const [pricesError, setPricesError] = useState<string | null>(null);
  const [enriching, setEnriching] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [priceTier, setPriceTier] = useState<CataloguePriceTier>('CATERING');
  const [items, setItems] = useState<CatalogueItem[]>([]);
  const [reloadToken, setReloadToken] = useState(0);

  const reloadQuiet = useCallback(() => {
    setReloadToken((n) => n + 1);
  }, []);

  useDemMasterDataRealtime({
    include: ['items', 'prices', 'standard_sales_lines'],
    onChange: reloadQuiet,
  });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setEnrichError(null);
    setPricesError(null);
    setEnriching(true);
    setPage(1);

    void loadCatalogueItems({
      onPartial: (partial) => {
        if (cancelled) return;
        setItems(partial);
        setLoading(false);
      },
    })
      .then(({ items: rows, enrichError: enrichErr, pricesError: priceErr }) => {
        if (cancelled) return;
        setItems(rows);
        setEnrichError(enrichErr);
        setPricesError(priceErr);
      })
      .catch((err) => {
        if (cancelled) return;
        setItems([]);
        setError(err instanceof Error ? err.message : 'Failed to load catalogue.');
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          setEnriching(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [reloadToken]);

  useEffect(() => {
    setPage(1);
  }, [search, priceTier]);

  const visible = useMemo(
    () => items.filter((item) => shouldShowCatalogueItem(item)),
    [items],
  );

  const filtered = useMemo(
    () => visible.filter((item) => matchesCatalogueSearch(item, search)),
    [visible, search],
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / CATALOGUE_PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = useMemo(() => {
    const start = (safePage - 1) * CATALOGUE_PAGE_SIZE;
    return filtered.slice(start, start + CATALOGUE_PAGE_SIZE);
  }, [filtered, safePage]);

  const from = filtered.length === 0 ? 0 : (safePage - 1) * CATALOGUE_PAGE_SIZE + 1;
  const to = Math.min(safePage * CATALOGUE_PAGE_SIZE, filtered.length);

  const leadText = loading
    ? 'Loading active items…'
    : enriching
      ? 'Syncing catalogue…'
      : `${visible.length.toLocaleString()} in catalogue`;

  return (
    <div className="catalogue-page">
      <div className="catalogue-page__sticky">
        <header className="catalogue-page__hero">
          <div className="catalogue-page__title-row">
            <p className="catalogue-page__eyebrow">Catalogue</p>
            <p className="catalogue-page__lead">{leadText}</p>
          </div>

          <div className="catalogue-page__controls">
            <div className="catalogue-page__tier-toggle" role="group" aria-label="Price group">
              <button
                type="button"
                className={`catalogue-page__tier-btn${priceTier === 'CATERING' ? ' catalogue-page__tier-btn--active' : ''}`}
                aria-pressed={priceTier === 'CATERING'}
                onClick={() => setPriceTier('CATERING')}
              >
                CATERING
              </button>
              <button
                type="button"
                className={`catalogue-page__tier-btn${priceTier === 'CATERING2' ? ' catalogue-page__tier-btn--active' : ''}`}
                aria-pressed={priceTier === 'CATERING2'}
                onClick={() => setPriceTier('CATERING2')}
              >
                CATERING2
                <span className="catalogue-page__tier-note">+10%</span>
              </button>
            </div>

            <label className="catalogue-page__search">
              <span className="sr-only">Search items</span>
              <input
                type="search"
                className="catalogue-page__search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by code, description, Chinese name or IPG"
                aria-label="Search catalogue"
                disabled={loading && items.length === 0}
              />
            </label>
          </div>
        </header>

        {error ? <p className="catalogue-page__error">{error}</p> : null}
        {enrichError ? (
          <p className="catalogue-page__warn">
            IPG / inventory from PowerBI unavailable: {enrichError}
          </p>
        ) : null}
        {pricesError ? (
          <p className="catalogue-page__warn">Price group prices unavailable: {pricesError}</p>
        ) : null}

        {!loading || items.length > 0 ? (
          <div className="catalogue-page__toolbar">
            <p className="catalogue-page__count">
              {filtered.length === 0
                ? 'No matches'
                : `Showing ${from.toLocaleString()}–${to.toLocaleString()} of ${filtered.length.toLocaleString()}`}
            </p>
          </div>
        ) : null}
      </div>

      <div className="catalogue-page__body">
        {loading && items.length === 0 ? (
          <div className="catalogue-grid catalogue-grid--skeleton" aria-busy>
            {Array.from({ length: 8 }, (_, i) => (
              <div key={i} className="catalogue-card catalogue-card--skeleton" />
            ))}
          </div>
        ) : pageItems.length === 0 && !error ? (
          <p className="catalogue-page__empty">
            {search.trim() ? 'No items match your search.' : 'No active items found.'}
          </p>
        ) : (
          <div className="catalogue-grid">
            {pageItems.map((item) => (
              <CatalogueCard key={item.code} item={item} priceTier={priceTier} />
            ))}
          </div>
        )}

        {totalPages > 1 && pageItems.length > 0 ? (
          <div className="catalogue-page__pager catalogue-page__pager--bottom">
            <button
              type="button"
              className="catalogue-page__pager-btn"
              disabled={safePage <= 1}
              onClick={() => {
                setPage((p) => Math.max(1, p - 1));
                scrollCatalogueToTop();
              }}
            >
              Prev
            </button>
            <span className="catalogue-page__pager-status">
              Page {safePage} of {totalPages}
            </span>
            <button
              type="button"
              className="catalogue-page__pager-btn"
              disabled={safePage >= totalPages}
              onClick={() => {
                setPage((p) => Math.min(totalPages, p + 1));
                scrollCatalogueToTop();
              }}
            >
              Next
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
